import React from "react";
import { View, StyleSheet } from "react-native";
import { colors, layout } from "@/constants";
import Text from "./base/Text";

/**
 * Props interface for the DrawerSection component
 */
interface DrawerSectionProps {
  /** Optional section title to display at the top */
  title?: string;
  /** Child components to render within the section */
  children: React.ReactNode;
  /** Whether to show a bottom border divider (default: false) */
  showDivider?: boolean;
  /** Whether this section should flex to fill available space */
  flex?: boolean;
}

/**
 * A reusable section wrapper component for drawer content.
 * Provides consistent spacing, optional titles, and dividers.
 *
 * @param props - The component props
 * @returns A section container with consistent drawer styling
 */
export default function DrawerSection({
  title,
  children,
  showDivider = false,
  flex = false
}: DrawerSectionProps) {
  const containerStyle = [
    styles.container,
    showDivider && styles.withDivider,
    flex && styles.flexContainer
  ];

  return (
    <View style={containerStyle}>
      {title && (
        <Text size="medium" color="secondary" style={styles.title}>
          {title}
        </Text>
      )}
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: layout.space.sm,
    // Horizontal padding kept minimal because drawer pressables (DrawerButton,
    // DrawerSession, headerContainer) already provide their own paddingHorizontal
    // for click-target comfort and hover-overlay shape.
    paddingHorizontal: layout.space.sm
  },
  withDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.outline
  },
  flexContainer: {
    flex: 1
  },
  title: {
    marginBottom: layout.space.md
  }
});
